/**
 * @file BoxSection.tsx
 * @summary Section page d'accueil — BoxSection
 *
 * Bloc de contenu affiché sur la home (`/`). Composant présentationnel utilisant les tokens du design system (Charbon/Or, Bebas Neue/Inter).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useState, useEffect, useCallback } from "react";
import Section from "@/components/Section";
import { Accessibility, ShowerHead, Wrench, ChevronLeft, ChevronRight, Disc, Circle, Target, Grip, Link2, Scissors, BarChart3, Bike, Wind, ShieldCheck, Swords, Square, LayoutGrid } from "lucide-react";
import photoBox2 from "@/assets/photo-box-2.webp";
import photoBox3 from "@/assets/photo-box-3.webp";
import photoBox4 from "@/assets/photo-box-4.webp";
import photoAthletes from "@/assets/photo-athletes-1.webp";
import photoWod1 from "@/assets/photo-wod-1.webp";
import photoSandbag from "@/assets/photo-sandbag.webp";
import photoAthletes3 from "@/assets/photo-athletes-3.webp";
import photoAthletes4 from "@/assets/photo-athletes-4.webp";
import photoPullups from "@/assets/photo-pullups.webp";
import photoSnatch from "@/assets/photo-snatch.webp";
import photoRower from "@/assets/photo-rower.webp";
import photoWorm from "@/assets/photo-worm.webp";
import photoEchoBike from "@/assets/photo-echo-bike.webp";

const equipment = [
  { name: "WORM", icon: Link2 },
  { name: "D-Balls", icon: Circle },
  { name: "Wall Balls", icon: Target },
  { name: "Dumbbells", icon: Grip },
  { name: "Kettlebells", icon: Disc },
  { name: "Racks de gym", icon: LayoutGrid },
  { name: "Barres & poids", icon: BarChart3 },
  { name: "Cordes", icon: Link2 },
  { name: "Cordes à sauter", icon: Scissors },
  { name: "Rameurs", icon: BarChart3 },
  { name: "Echo Bikes", icon: Bike },
  { name: "Ski Erg", icon: Wind },
  { name: "Gilets lestés", icon: ShieldCheck },
  { name: "Sleds", icon: Swords },
  { name: "Sac de frappe", icon: Square },
  { name: "Tapis de gym", icon: Square },
];

const facilities = [
  { icon: ShowerHead, text: "Vestiaires H/F, douches, toilettes" },
  { icon: Accessibility, text: "Accessibilité PMR" },
  { icon: Wrench, text: "Matériel professionnel complet" },
];

const photos = [
  { src: photoBox2, alt: "La box CrossFit Chistera" },
  { src: photoAthletes, alt: "Athlètes en action" },
  { src: photoWod1, alt: "WOD en cours" },
  { src: photoPullups, alt: "Pull-ups" },
  { src: photoSnatch, alt: "Snatch — haltérophilie" },
  { src: photoRower, alt: "Rameur Concept 2" },
  { src: photoWorm, alt: "WORM carry" },
  { src: photoSandbag, alt: "Sandbag carry" },
  { src: photoBox3, alt: "Équipement CrossFit Chistera" },
  { src: photoAthletes3, alt: "Athlètes — dumbbell, echo bike" },
  { src: photoAthletes4, alt: "Athlètes féminines" },
  { src: photoBox4, alt: "Vue intérieure de la box" },
  { src: photoEchoBike, alt: "Echo Bike" },
];

export default function BoxSection() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % photos.length);
  }, []);

  const prev = () => {
    setCurrent((c) => (c - 1 + photos.length) % photos.length);
  };

  useEffect(() => {
    const interval = setInterval(next, 4000);
    return () => clearInterval(interval);
  }, [next]);

  const getVisible = (count: number) => {
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(photos[(current + i) % photos.length]);
    }
    return result;
  };

  return (
    <Section id="box" className="bg-card">
      <div className="text-center mb-12">
        <span className="inline-block text-sm font-semibold text-gold tracking-wider mb-2 font-body normal-case">Saint-Pierre-de-Chandieu · Est Lyonnais</span>
        <h2 className="text-4xl md:text-5xl text-foreground">La box</h2>
        <p className="text-muted-foreground mt-3 font-body normal-case text-sm max-w-2xl mx-auto">
          Salle affiliée depuis avril 2023, CrossFit Chistera vous accueille à Saint-Pierre-de-Chandieu (Auvergne-Rhône-Alpes) dans un espace de 250 m² entièrement équipé
        </p>
      </div>

      {/* Photos carousel */}
      <div className="relative mb-12">
        <div className="hidden md:flex gap-3">
          {getVisible(3).map((p, i) => (
            <div key={`${current}-${i}`} className="flex-1 aspect-[4/3] rounded-lg overflow-hidden">
              <img src={p.src} alt={p.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
        <div className="md:hidden">
          <div className="aspect-[4/3] rounded-lg overflow-hidden">
            <img src={photos[current].src} alt={photos[current].alt} className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-charcoal/70 text-white rounded-full p-2 hover:bg-charcoal transition-colors" aria-label="Précédent">
          <ChevronLeft size={20} />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-charcoal/70 text-white rounded-full p-2 hover:bg-charcoal transition-colors" aria-label="Suivant">
          <ChevronRight size={20} />
        </button>
        <div className="flex justify-center gap-1.5 mt-3">
          {photos.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-gold" : "bg-border"}`} aria-label={`Photo ${i + 1}`} />
          ))}
        </div>
      </div>

      {/* Facilities */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {facilities.map((f) => (
          <div key={f.text} className="flex items-center gap-3 bg-background border border-border rounded-lg p-4">
            <f.icon className="text-gold shrink-0" size={22} />
            <span className="text-sm text-foreground font-body normal-case">{f.text}</span>
          </div>
        ))}
      </div>

      {/* Equipment — icon grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
        {equipment.map((e) => (
          <div key={e.name} className="flex items-center gap-2.5 text-sm font-body normal-case text-foreground">
            <e.icon size={14} className="text-gold shrink-0" />
            <span>{e.name}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}
